import type { TFunction } from 'i18next';
import { PickCombo } from '@components/ui/PickCombo/PickCombo';
import type { DeliveryBoardStatus } from '@interfaces/ILogistics';
import { displayStatusName } from '../board.logic';
import styles from './DeliveryOrderForm.module.css';

interface IDeliveryStatusFieldProps {
  fieldId: string;
  /** The column the trigger names — already fallback-resolved by the form. */
  selectedStatus: DeliveryBoardStatus | null;
  boardStatuses: DeliveryBoardStatus[];
  /** An order with ANY synced line has its status frozen (one-unsynced-order
   *  rule): render the column as a read-only value, no combo, and say why. */
  locked?: boolean;
  onPick: (statusId: string) => void;
  t: TFunction;
}

/**
 * The edit form's board-column picker — extracted from `DeliveryOrderForm`
 * (which sits against the 600-line gate). The shared ui/PickCombo rather than
 * a native <select>, so the two logistics dialogs render one control
 * vocabulary. Opens upward: it is the form's bottom-most field.
 */
export function DeliveryStatusField({ fieldId, selectedStatus, boardStatuses, locked, onPick, t }: IDeliveryStatusFieldProps) {
  if (locked) {
    return (
      <div className={styles.field}>
        <span className={styles.label}>{t('logistics.orderDialog.status')}</span>
        <span className={`${styles.input} ${styles.lockedValue}`} aria-describedby={`${fieldId}-status-locked`}>
          {selectedStatus ? displayStatusName(selectedStatus, t) : ''}
        </span>
        <span id={`${fieldId}-status-locked`} className={styles.fieldHint}>
          {t('logistics.orderDialog.statusLocked')}
        </span>
      </div>
    );
  }
  return (
    <div className={styles.field}>
      <label className={styles.label} htmlFor={`${fieldId}-status`}>
        {t('logistics.orderDialog.status')}
      </label>
      <PickCombo
        id={`${fieldId}-status`}
        value={selectedStatus ? selectedStatus.id : ''}
        options={boardStatuses.map((s) => ({ value: s.id, label: displayStatusName(s, t) }))}
        placeholder=""
        onPick={onPick}
        dropUp
      />
    </div>
  );
}
